import type { ProfileMediaType } from "../types/profile";

/**
 * 作品タグ名を表示用に正規化する（先頭の `#` と前後空白を除去）。
 *
 * BE 側でも `#` を付与しないように統一済みだが、レガシー由来のデータや手入力に
 * 備えた防御として FE 側でも除去している。
 */
export const stripTagHash = (tag: string): string =>
  tag.replace(/^#+/, "").trim();

/**
 * 作品タグ配列を編集・表示用に正規化する（各要素の `#` 除去 + 空要素除外）。
 *
 * `WorkEditModal` のタグ欄と `MySiteWorks` 表のタグ列で共通利用し、
 * 旧環境由来の `#` 付きハッシュタグをどちらの編集経路でも吸収する。
 */
export const normalizeTagList = (tags: string[]): string[] =>
  tags.map(stripTagHash).filter(Boolean);

/**
 * `ProfileMediaType` に対応する Material Symbols のアイコン名を返す。
 *
 * マイサイト編集（`MySiteWorks` / `WorkEditModal`）と公開プロフィールで、
 * メディア種別アイコンの表記を統一するためのヘルパ。
 */
export const profileMediaIcon = (type: ProfileMediaType): string => {
  switch (type) {
    case "audio":
      return "music_note";
    case "video":
      return "movie";
    case "image":
      return "image";
    case "pdf":
      return "picture_as_pdf";
    default:
      return "attach_file";
  }
};

/**
 * 入力 `File` から作品メディアの種別（`ProfileMediaType`）を判定する。
 *
 * MIME タイプ（`file.type`）を優先し、空文字の場合は拡張子にフォールバックする。
 * いずれも該当しない場合は既存仕様に合わせて `'audio'` を返す。
 */
export const detectProfileMediaType = (file: File): ProfileMediaType => {
  if (file.type.startsWith("audio/")) return "audio";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("image/")) return "image";
  if (file.type === "application/pdf") return "pdf";

  const ext = file.name.toLowerCase().split(".").pop() || "";
  if (["mp3", "wav", "m4a", "aac", "flac", "ogg"].includes(ext)) return "audio";
  if (["mp4", "mov", "webm", "avi", "mkv"].includes(ext)) return "video";
  if (["jpg", "jpeg", "png", "webp", "gif", "bmp"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "audio";
};
